import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import moment from 'moment';
import {
  getAcademies,
} from '../../axios/academiesRequests';
import {
  createAd, getAdById, getAds, sendAdsNotification, updateAd, updateAdStatus,
} from '../../axios/bannerAdsRequests';
import {
  getBranches,
} from '../../axios/branchesRequests';
import {
  getDeglawyOffers, getDeglawyPartners,
} from '../../axios/deglawyRequests';
import {
  getDeglawyTrophies,
} from '../../axios/deglawyTrophiesRequests';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  convertCountriesToISOCodeString,
} from '../../constants/countries';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import {
  ICountry,
} from '../storeInterfaces/authInterface';
import {
  DropDownItem, IAd, IBannerAdsStore,
} from '../storeInterfaces/bannerAdsInterface';
import { RequestStatus } from 'mobx-store';

export class BannerAdsStore implements IBannerAdsStore {
  @observable adsList: any[] = [];

  @observable selectedAd = {
  };

  @observable loading = false;

  @observable entityList: DropDownItem[] = [];

  @observable getAdsRequestStatus: RequestStatus = RequestStatus.IDLE; 

  private static restructureAdsList(ads: IAd[]): any[] {
    return ads.map((ad: IAd) => ({
      adId: ad.advertisementId,
      title: ad.title,
      isPublished: ad.isPublished,
      startDate: moment(ad.startDate).format('YYYY-MM-DD'),
      endDate: moment(ad.endDate).format('YYYY-MM-DD'),
    }));
  }

  private static restructureCreateOrUpdateAd(ad: any): any {
    return {
      title: ad.title,
      adType: ad.adTypeList,
      entityType: ad.entityTypeList ? ad.entityTypeList : null,
      entityContent: ad.entityContentList ? ad.entityContentList : null,
      externalLink: ad.externalLink ? ad.externalLink : null,
      backgroundImageURL: ad.backgroundImage,
      isPublished: ad.isPublished,
      autoScheduled: ad.autoScheduled.toggle,
      startDate: ad.autoScheduled.startDate.ts ? ad.autoScheduled.startDate.ts : moment(ad.autoScheduled.startDate).valueOf(),
      endDate: ad.autoScheduled.endDate.ts ? ad.autoScheduled.endDate.ts : moment(ad.autoScheduled.endDate).valueOf(),
      availableCountries: ad.availableCountries,
    };
  }

  private static restructureAd(ad: any): any {
    return {
      adId: ad.advertisementId,
      adTypeList: ad.type,
      entityTypeList: ad.entityType ? ad.entityType : null,
      entityContentList: ad.entityId ? ad.entityId : null,
      backgroundImage: (ad.backgroundImageURL ? [{
        imageUrl: ad.backgroundImageURL,
      }] : []),
      title: ad.title,
      externalLink: ad.externalLink ? ad.externalLink : null,
      isPublished: ad.isPublished,
      autoScheduled: {
        toggle: ad.autoScheduled,
        startDate: ad.startDate ? new Date(ad.startDate) : null,
        endDate: ad.endDate ? new Date(ad.endDate) : null,
      },
    };
  }

  @action
  public async getAds(countries: ICountry[], entityType: string[], langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      this.getAdsRequestStatus = RequestStatus.LOADING;
      const availableCountries = convertCountriesToISOCodeString(countries);

      const params: any = {
        availableCountries,
        status: 'all',
      };
      params.entityType = entityType || null;
      const config = {
        ...getHeader(langCode), params,
      };

      const response = await Connections.wadiDeglaAxios.httpGet(getAds, config);
      this.adsList = BannerAdsStore.restructureAdsList(response.data);
      this.getAdsRequestStatus =  RequestStatus.SUCCESS;
    } catch (error) {
      this.getAdsRequestStatus = RequestStatus.FAILED;
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getAdById(id: number, langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getAdById(id), getHeader(langCode));
      this.selectedAd = BannerAdsStore.restructureAd(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createAd(newAd: any, langCode: string = DefaultLanguage): Promise<any> {
    return new Promise<void>(async (res, rej) => {
      try {
        newAd.backgroundImage = await this.uploadSingleImage(newAd.backgroundImage);
        const response = await Connections.wadiDeglaAxios.httpPost(createAd, BannerAdsStore.restructureCreateOrUpdateAd(newAd), getHeader(langCode));
        res(response.data.advertisementId);
      } catch (error) {
        rej(error);
      }
    });
  }

  @action.bound
  public async updateAd(updatedAd: any, id: number, langCode: string = DefaultLanguage): Promise<any> {
    try {
      updatedAd.backgroundImage = await this.uploadSingleImage(updatedAd.backgroundImage);
      await Connections.wadiDeglaAxios.httpPut(updateAd(id), BannerAdsStore.restructureCreateOrUpdateAd(updatedAd), getHeader(langCode));
    } catch (error) {
      console.log(error);
    }
  }

  @action.bound
  public async updateAdStatus(updatedAdStatus: boolean, index: number): Promise<any> {
    try {
      await Connections.wadiDeglaAxios.httpPut(updateAdStatus(this.adsList[index].adId), {
        isPublished: updatedAdStatus,
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.adsList[index].isPublished = updatedAdStatus;
    }
  }

  @action
  public async getAcademies(countries: ICountry[], langCode: string = DefaultLanguage): Promise<void> {
    try {
      this.loading = true;
      const countriesIsoCode = countries.map((country: ICountry) => country.isoCode);
      const availableCountries = countriesIsoCode.toString();

      const response = await Connections.wadiDeglaAxios.httpGet(getAcademies(availableCountries), getHeader(langCode));
      this.entityList = this.restructureAcademiesList(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getBranches(countries: ICountry[], langCode: string = DefaultLanguage, status = 'all'): Promise<void> {
    try {
      this.loading = true;
      const availableCountries = convertCountriesToISOCodeString(countries);
      const params = {
        availableCountries,
        status,
      };
      const config = {
        ...getHeader(langCode), params,
      };

      const response = await Connections.wadiDeglaAxios.httpGet(getBranches, config);
      this.entityList = this.restructureBranchesList(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  public async sendAdNotification(adId: number, title: string, body: string, status: string, image: File[]): Promise<any> {
    try {
      const adImage = await this.uploadSingleImage(image);
      const data = {
        title,
        body,
        imageURL: adImage,
        audience: status,
      };
      const response = await Connections.wadiDeglaAxios.httpPost(sendAdsNotification(), data);
    } catch (error) {
      console.log(error);
    }
  }

  @action
  public async getDeglawyOffers(langCode: string = DefaultLanguage): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getDeglawyOffers, getHeader(langCode));
      this.entityList = this.mapOffersData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getDeglawyPartners(langCode: string = DefaultLanguage): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getDeglawyPartners, getHeader(langCode));
      this.entityList = this.mapPartnersData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getDeglawyTrophies(langCode: string = DefaultLanguage): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getDeglawyTrophies, getHeader(langCode));
      this.entityList = this.mappingResponseToDeglawyTrophies(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  private mappingResponseToDeglawyTrophies(deglawyTrophiesResponse: any[]): DropDownItem[] {
    let deglawyTrophiesList: DropDownItem[] = [];
    deglawyTrophiesList = deglawyTrophiesResponse.map((trophy) => ({
      id: trophy.deglawyTrophyId,
      title: trophy.title,
    }));
    return deglawyTrophiesList;
  }

  private mapPartnersData(response: any) {
    let deglawyPartnersList: DropDownItem[] = [];
    deglawyPartnersList = response.map((partner: any) => ({
      id: partner.deglawyPartnerId,
      title: partner.name,
    }));
    return deglawyPartnersList;
  }

  private mapOffersData(response: any) {
    let deglawyOffersList: DropDownItem[] = [];
    deglawyOffersList = response.map((offer: any) => ({
      id: offer.offerId,
      title: `${offer.partner.name}, ${offer.title}`,
    }));
    return deglawyOffersList;
  }

  private restructureAcademiesList(academies: any[]): DropDownItem[] {
    let academiesList: DropDownItem[] = [];
    academiesList = academies.map((academy: any) => ({
      id: academy.academyId,
      title: academy.title,
    }));
    return academiesList;
  }

  private restructureBranchesList(branches: any[]): DropDownItem[] {
    let branchesList: DropDownItem[] = [];
    branchesList = branches.map((branch: any) => ({
      id: branch.branchId,
      title: branch.title,
    }));
    return branchesList;
  }

  private uploadSingleImage(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push(res.data.downloadURL);
            }),
        );
      } else {
        allImages.push(image.imageUrl);
      }
    }
    return Promise.all(promises)
      .then(() => (allImages.length ? allImages[0] : null)).catch((error) => (allImages.length ? allImages[0] : null));
  }
}
